import { useEffect, useCallback } from 'react';
import { GameState } from '../types/game';
import { createKeyboardEventCleanup } from '../utils/effectCleanup';

interface UseKeyboardHandlerProps {
  gameState: GameState;
  isSubmitting: boolean;
  updateGameState: (updates: Partial<GameState>) => void;
  triggerHapticFeedback: (type?: 'light' | 'medium' | 'heavy') => void;
  triggerShake: () => void;
  onEnter: () => void;
  onDelete: () => void;
}

/**
 * Custom hook for handling keyboard interactions
 * Extracted from GamePage to improve maintainability
 */
export const useKeyboardHandler = ({
  gameState,
  isSubmitting,
  updateGameState,
  triggerHapticFeedback,
  triggerShake,
  onEnter,
  onDelete
}: UseKeyboardHandlerProps) => {

  // Handle key press for letters and symbols
  const handleKeyPress = useCallback((key: string) => {
    if (gameState.gameStatus !== 'playing' || isSubmitting) return;

    if (gameState.currentGuess.length < 5) {
      updateGameState({
        currentGuess: gameState.currentGuess + key,
        error: null
      });

      // Trigger haptic feedback for virtual keyboard
      triggerHapticFeedback('light');

      // Announce key press to screen readers
      const announcement = document.createElement('div');
      announcement.setAttribute('aria-live', 'polite');
      announcement.setAttribute('aria-atomic', 'true');
      announcement.className = 'sr-only';
      announcement.textContent = `${key} entered`;
      document.body.appendChild(announcement);
      setTimeout(() => document.body.removeChild(announcement), 500);
    } else {
      // Provide feedback when trying to enter more letters than allowed
      triggerShake();

      const announcement = document.createElement('div');
      announcement.setAttribute('aria-live', 'assertive');
      announcement.setAttribute('aria-atomic', 'true');
      announcement.className = 'sr-only';
      announcement.textContent = 'Maximum letters reached';
      document.body.appendChild(announcement);
      setTimeout(() => document.body.removeChild(announcement), 1000);
    }
  }, [gameState.gameStatus, gameState.currentGuess, updateGameState, isSubmitting, triggerHapticFeedback, triggerShake]);

  // Handle enter key press
  const handleEnter = useCallback(() => {
    if (gameState.gameStatus !== 'playing' || isSubmitting) return;

    if (!gameState.currentGuess.trim()) {
      // Provide feedback for empty guess
      triggerShake();

      const announcement = document.createElement('div');
      announcement.setAttribute('aria-live', 'assertive');
      announcement.setAttribute('aria-atomic', 'true');
      announcement.className = 'sr-only';
      announcement.textContent = 'Please enter a guess before submitting';
      document.body.appendChild(announcement);
      setTimeout(() => document.body.removeChild(announcement), 1000);

      return;
    }

    // Trigger haptic feedback for submission
    triggerHapticFeedback('medium');

    onEnter();
    updateGameState({ currentGuess: '' });
  }, [gameState.gameStatus, gameState.currentGuess, isSubmitting, triggerHapticFeedback, triggerShake, onEnter, updateGameState]);

  // Handle delete key press
  const handleDelete = useCallback(() => {
    if (gameState.gameStatus !== 'playing' || isSubmitting) return;

    if (gameState.currentGuess.length > 0) {
      updateGameState({
        currentGuess: gameState.currentGuess.slice(0, -1),
        error: null
      });

      // Trigger haptic feedback for deletion
      triggerHapticFeedback('light');

      // Announce deletion to screen readers
      const announcement = document.createElement('div');
      announcement.setAttribute('aria-live', 'polite');
      announcement.setAttribute('aria-atomic', 'true');
      announcement.className = 'sr-only';
      announcement.textContent = 'Letter deleted';
      document.body.appendChild(announcement);
      setTimeout(() => document.body.removeChild(announcement), 500);
    }

    onDelete();
  }, [gameState.gameStatus, gameState.currentGuess, isSubmitting, triggerHapticFeedback, updateGameState, onDelete]);

  // Handle physical keyboard events
  useEffect(() => {
    const handlePhysicalKeyboard = (event: KeyboardEvent) => {
      // Only handle keyboard events when the game is active
      if (gameState.gameStatus !== 'playing' || isSubmitting) {
        return;
      }

      // Don't interfere with typing in input fields
      const target = event.target as HTMLElement;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.contentEditable === 'true')) {
        return;
      }

      // Allow browser/system shortcuts like copy/paste to keep working
      if (event.ctrlKey || event.metaKey) {
        return;
      }

      // Prevent default behavior for game keys to avoid conflicts
      const key = event.key.toUpperCase();

      if (key === 'ENTER') {
        event.preventDefault();
        triggerHapticFeedback('medium');
        handleEnter();
      } else if (key === 'BACKSPACE') {
        event.preventDefault();
        triggerHapticFeedback('light');
        handleDelete();
      } else if (/^[A-Z]$/.test(key)) {
        event.preventDefault();
        triggerHapticFeedback('light');
        handleKeyPress(key);
      } else if (['.', '$', '%', '+', '-'].includes(event.key)) {
        event.preventDefault();
        triggerHapticFeedback('light');
        handleKeyPress(event.key);
      }
    };

    // Use cleanup utility for proper event listener management
    return createKeyboardEventCleanup(
      document,
      handlePhysicalKeyboard
    );
  }, [
    gameState.gameStatus,
    isSubmitting,
    handleDelete,
    handleEnter,
    handleKeyPress,
    triggerHapticFeedback
  ]); // Keep all dependencies for correctness

  return {
    handleKeyPress,
    handleEnter,
    handleDelete,
  };
};
